/**
 * 代理商合約期限的徽章判定（批107）。
 *
 * 合約是**純資訊**：到期不停權、不影響任何權限（要停權仍是 `active=false`）。這裡只決定
 * 清單上那顆小徽章長什麼樣——判定抽成純函式是為了測得到「邊界那一天算哪邊」。
 *
 * ★ 全部用 `YYYY-MM-DD` 字串比較，不轉 `Date`：`new Date('2026-12-31')` 是 **UTC 午夜**，
 *   在台北時區會變成當地的 12/31 08:00，跨日判斷會在每天早上八點前後給出不同答案。
 *   字串比較沒有這個問題（同樣格式、同樣長度，字典序＝日期序）。
 */
export type ContractTone = 'ok' | 'soon' | 'expired' | 'none'

export interface ContractBadge {
  tone: ContractTone
  /** 徽章文字；`tone === 'none'`（沒填合約）時為空字串。 */
  label: string
}

/** 今天（本地時區）的 `YYYY-MM-DD`。 */
export function todayLocal(now: Date = new Date()): string {
  const p = (n: number) => String(n).padStart(2, '0')
  return `${now.getFullYear()}-${p(now.getMonth() + 1)}-${p(now.getDate())}`
}

/** `YYYY-MM-DD` 加 n 天（純字串進出，經 UTC 運算避免夏令時間位移）。 */
function addDays(day: string, n: number): string {
  const d = new Date(`${day}T00:00:00Z`)
  d.setUTCDate(d.getUTCDate() + n)
  return d.toISOString().slice(0, 10)
}

/**
 * @param endsOn 合約迄日（`YYYY-MM-DD`；null／空＝沒填）
 * @param today  今天（預設取本地時區）
 * @param warnDays 幾天內算「即將到期」（預設 30）
 */
export function contractBadge(
  endsOn: string | null | undefined,
  today: string = todayLocal(),
  warnDays = 30,
): ContractBadge {
  if (!endsOn) return { tone: 'none', label: '' }
  // 到期日**當天仍算有效**（合約寫到 12/31 就是含 12/31）。
  if (endsOn < today) return { tone: 'expired', label: '已到期' }
  if (endsOn <= addDays(today, warnDays)) return { tone: 'soon', label: '即將到期' }
  return { tone: 'ok', label: '' }
}
